import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Alert, Card, Typography, Space, Divider, Select, Tag, Upload, Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { UploadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { updateBlog, clearError, clearSuccess } from '../../features/blog/blogSlice';
import Editor from '../other/Editor'; // Update this path to your Editor component
import { predefinedTags } from '../../assets/data/predefined-tags';
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  FileTextOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;

const EditBlogForm = ({ blog, onClose }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { updating, success, error } = useSelector((state) => state.blogs);
  
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [wordCount, setWordCount] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form with blog data
  useEffect(() => {
    if (blog) {
      form.setFieldsValue({
        title: blog.title,
        summary: blog.summary,
        category: blog.category,
        status: blog.status,
        blogTags: blog.tags || []
      });
      setContent(blog.content || '');
      setTags(blog.tags || []);
      setWordCount(countWords(blog.content || ''));
    }
  }, [blog, form]);

  // Handle success
  useEffect(() => {
    if (success) {
      setHasChanges(false);
      
      setTimeout(() => {
        dispatch(clearSuccess());
        if (onClose) {
          onClose();
        }
      }, 2000);
    }
  }, [success, dispatch, onClose]);

  // Track changes to enable/disable save button
  useEffect(() => {
    const currentValues = form.getFieldsValue();
    const currentContent = content;
    const currentTags = tags;

    if (blog) {
      const hasFormChanges = 
        currentValues.title !== blog.title ||
        currentValues.summary !== blog.summary ||
        currentValues.category !== blog.category ||
        currentValues.status !== blog.status ||
        currentContent !== blog.content ||
        JSON.stringify(currentTags) !== JSON.stringify(blog.tags || []);
      
      setHasChanges(hasFormChanges);
    }
  }, [form, content, tags, blog]);

  // Count words in content
  const countWords = (html) => {
    const text = html.replace(/<[^>]*>/g, '').trim();
    return text ? text.split(/\s+/).length : 0;
  };

  // Handle content change
  const handleContentChange = (value) => {
    setContent(value);
    setWordCount(countWords(value));
  };

  // Handle form submission
  const handleFormSubmit = async (values) => {
    try {
      const updateData = {
        title: values.title,
        summary: values.summary,
        content: content,
        category: values.category,
        status: values.status,
        tags: tags
      };

      dispatch(updateBlog({ blogId: blog._id, blogData: updateData }));
    } catch (err) {
      console.error('Error updating blog:', err);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  // Custom close confirmation modal
  const showCloseConfirm = () => {
    confirm({
      title: 'Unsaved Changes',
      icon: <ExclamationCircleOutlined className="text-yellow-500" />,
      content: (
        <div className="py-2">
          <p className="text-gray-700 mb-2">
            You have unsaved changes that will be lost if you close now.
          </p>
          <p className="text-gray-600 text-sm">
            Are you sure you want to close without saving?
          </p>
        </div>
      ),
      okText: 'Yes, Close',
      cancelText: 'Keep Editing',
      okType: 'danger',
      onOk() {
        if (onClose) {
          dispatch(clearError());
          dispatch(clearSuccess());
          onClose();
        }
      },
      onCancel() {
        // User chose to keep editing
      },
    });
  };

  // Handle closing with unsaved changes
  const handleClose = () => {
    if (hasChanges) {
      showCloseConfirm();
    } else if (onClose) {
      dispatch(clearError());
      dispatch(clearSuccess());
      onClose();
    }
  };

  

  if (!blog) {
    return (
      <div className="flex items-center justify-center h-64">
        <Text className="text-text-secondary">No blog selected for editing</Text>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 bg-bg-primary">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <Space direction="vertical" size="small">
            <div className="flex items-center space-x-2">
              <EditOutlined className="text-2xl text-accent-blue" />
              <Title level={2} className="m-0 text-text-primary font-semibold">
                Edit Blog Post
              </Title>
            </div>
            <Text className="text-text-secondary text-base">
              Update and modify your existing blog post
            </Text>
          </Space>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={handleClose}
            className="text-text-secondary hover:text-accent-red hover:bg-accent-red/10"
          >
            Close
          </Button>
        </div>

        {/* Blog Info */}
        <Card className="shadow-soft border border-border-light rounded-lg bg-blue-50 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-text-secondary text-sm">Currently Editing:</Text>
              <Title level={4} className="m-0 text-text-primary">
                {blog.title}
              </Title>
              <Text className="text-text-secondary text-sm">
                {blog.publishedAt && (
                  <>Published: {new Date(blog.publishedAt).toLocaleDateString()} • </>
                )}
                {blog.views || 0} views
              </Text>
            </div>
            {hasChanges && (
              <Tag color="orange" className="animate-pulse">
                Unsaved Changes
              </Tag>
            )}
          </div>
        </Card>
      </div>

      {/* Alert Messages */}
      {success && (
        <Alert
          className="mb-6 border border-accent-green bg-green-50 rounded-lg"
          message="Blog post updated successfully!"
          description="Your changes have been saved and the blog post has been updated."
          type="success"
          showIcon
          closable
          onClose={() => dispatch(clearSuccess())}
        />
      )}
      
      {error && (
        <Alert
          className="mb-6 border border-accent-red bg-red-50 rounded-lg"
          message="Update Failed"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => dispatch(clearError())}
        />
      )}

      {/* Main Form Card */}
      <Card 
        className="shadow-medium border border-border-light rounded-xl bg-white"
        bodyStyle={{ padding: '32px' }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          onFinishFailed={onFinishFailed}
          size="large"
          className="space-y-6"
        >
          {/* Basic Information Section */}
          <div className="mb-8">
            <Title level={4} className="text-text-primary mb-4 flex items-center">
              <CheckCircleOutlined className="mr-2 text-accent-blue" />
              Blog Information
            </Title>
            
            <Form.Item 
              label={<span className="text-text-primary font-medium">Blog Title</span>}
              name="title" 
              rules={[{ required: true, message: 'Please enter a blog title' }]}
              className="mb-6"
            >
              <Input 
                placeholder="Enter an engaging title for your blog post"
                className="h-12 border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue"
              />
            </Form.Item>

            <Form.Item 
              label={<span className="text-text-primary font-medium">Blog Summary</span>}
              name="summary" 
              rules={[{ required: true, message: 'Please enter a summary' }]}
              className="mb-6"
            >
              <TextArea 
                rows={3}
                placeholder="Write a compelling summary that will appear in blog previews..."
                className="border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue resize-none"
              />
            </Form.Item>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Form.Item 
                label={<span className="text-text-primary font-medium">Category</span>}
                name="category"
                rules={[{ required: true, message: 'Please select a category' }]}
              >
                <Select 
                  placeholder="Select blog category"
                  className="h-12"
                >
                  <Select.Option value="technology">💻 Technology</Select.Option>
                  <Select.Option value="design">🎨 Design</Select.Option>
                  <Select.Option value="development">⚡ Development</Select.Option>
                  <Select.Option value="career">🚀 Career</Select.Option>
                  <Select.Option value="tutorial">📚 Tutorial</Select.Option>
                  <Select.Option value="personal">👤 Personal</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item 
                label={<span className="text-text-primary font-medium">Status</span>}
                name="status"
              >
                <Select 
                  placeholder="Select status"
                  className="h-12"
                >
                  <Select.Option value="draft">📝 Draft</Select.Option>
                  <Select.Option value="published">✅ Published</Select.Option>
                  <Select.Option value="archived">📦 Archived</Select.Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item 
              label={<span className="text-text-primary font-medium">Tags</span>}
              name="blogTags"
            >
              <Select
                mode="multiple"
                placeholder="Select or add tags"
                value={tags}
                onChange={setTags}
                className="min-h-12"
                options={predefinedTags.map(tag => ({ label: tag, value: tag }))}
              />
            </Form.Item>
          </div>

          <Divider className="border-border-light" />

         {/* Content Editor Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <Title level={4} className="text-text-primary mb-0 flex items-center">
                <FileTextOutlined className="mr-2 text-accent-blue" />
                Blog Content
              </Title>
              <div className="flex items-center space-x-4">
                <Text className="text-text-secondary text-sm">
                  {wordCount > 0 && (
                    <>
                      <strong>{wordCount}</strong> words • 
                      <span className="text-accent-blue">
                        ~{Math.ceil(wordCount / 200)} min read
                      </span>
                    </>
                  )}
                </Text>
              </div>
            </div>
            
            {/* Enhanced Editor with sticky toolbar and fixed height */}
            <div className="editor-container border border-gray-200 rounded-lg" style={{ height: '700px', display: 'flex', flexDirection: 'column' }}>
              <Editor
                value={content}
                onChange={handleContentChange}
                placeholder="Edit your blog content here..."
                minHeight="400px"
                stickyToolbar={true}
                style={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                editorStyle={{
                  height: 'calc(100% - 60px)', // Subtract toolbar height
                  overflow: 'auto',
                  border: 'none'
                }}
                toolbarStyle={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  backgroundColor: 'white',
                  borderBottom: '1px solid #e8e8e8',
                  borderRadius: '8px 8px 0 0'
                }}
                onClear={() => setContent('')}
              />
            </div>

            {/* Media Rendering Guide Section */}
            <Card className="mt-4 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
              <div className="mb-3">
                <Title level={5} className="mb-2 text-amber-900 flex items-center">
                  <EditOutlined className="mr-2" />
                  Media Rendering Guide
                </Title>
                <Text className="text-amber-700 text-sm">
                  Enhance your blog content with rich media while editing
                </Text>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Regular Media */}
                <div className="bg-white p-4 rounded-lg border border-amber-100">
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <Text strong className="text-green-700">Regular Media</Text>
                  </div>
                  <Text className="text-sm text-gray-600 mb-3">
                    Single images/videos display as centered blocks
                  </Text>
                  <div className="bg-gray-50 p-2 rounded text-xs font-mono">
                    Just paste the URL directly in text:<br/>
                    <span className="text-blue-600">https://example.com/update.jpg</span>
                  </div>
                </div>

                {/* Carousel Media */}
                <div className="bg-white p-4 rounded-lg border border-amber-100">
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                    <Text strong className="text-purple-700">Carousel Media</Text>
                  </div>
                  <Text className="text-sm text-gray-600 mb-3">
                    Multiple media in horizontal scrollable layout
                  </Text>
                  <div className="bg-gray-50 p-2 rounded text-xs font-mono">
                    Create a list with media URLs:<br/>
                    • <span className="text-blue-600">https://before.jpg</span><br/>
                    • <span className="text-blue-600">https://process.jpg</span><br/>
                    • <span className="text-blue-600">https://after.mp4</span>
                  </div>
                </div>

                {/* Grid Media */}
                <div className="bg-white p-4 rounded-lg border border-amber-100">
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                    <Text strong className="text-orange-700">Grid Media</Text>
                  </div>
                  <Text className="text-sm text-gray-600 mb-3">
                    Multiple media in organized grid layout
                  </Text>
                  <div className="bg-gray-50 p-2 rounded text-xs font-mono">
                    Start with layout config line:<br/>
                    • <span className="text-orange-600">layout=[grid] col=[2]</span><br/>
                    • <span className="text-blue-600">https://version1.jpg</span><br/>
                    • <span className="text-blue-600">https://version2.jpg</span><br/>
                    • <span className="text-blue-600">https://version3.jpg</span><br/>
                    • <span className="text-blue-600">https://final.jpg</span>
                  </div>
                </div>
              </div>

              {/* Supported Formats */}
              <div className="mt-4 p-3 bg-white rounded-lg border border-amber-100">
                <Text strong className="text-amber-900 block mb-2">Supported Formats:</Text>
                <div className="flex flex-wrap gap-2">
                  <Tag color="green">Images: .jpg, .png, .gif, .webp, .svg</Tag>
                  <Tag color="blue">Videos: .mp4, .webm, .ogg, .mov</Tag>
                  <Tag color="red">YouTube: youtube.com or youtu.be links</Tag>
                </div>
              </div>

              {/* Edit-Specific Pro Tips */}
              <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                <Text strong className="text-orange-800 block mb-2">✏️ Editing Pro Tips:</Text>
                <ul className="text-sm text-orange-700 space-y-1 ml-4">
                  <li>• Add new media to illustrate updated content or examples</li>
                  <li>• Replace outdated screenshots with current versions</li>
                  <li>• Use grids to show evolution: old vs new comparisons</li>
                  <li>• Create carousels for step-by-step process updates</li>
                  <li>• Add context text before media URLs to explain changes</li>
                  <li>• Mix new media with existing content for comprehensive updates</li>
                  <li>• Perfect for refreshing tutorials with latest information</li>
                  <li>• Use "layout=[grid] col=[2]" for before/after editing showcases</li>
                  <li>• All layouts remain mobile-responsive after updates</li>
                </ul>
              </div>
            </Card>
          </div>

          {/* Tags Display */}
          {tags.length > 0 && (
            <div className="mb-6">
              <Text className="text-text-primary font-medium text-sm uppercase tracking-wide block mb-3">
                Selected Tags
              </Text>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag, index) => (
                  <Tag 
                    key={index}
                    color="blue"
                    className="px-3 py-1 text-sm"
                  >
                    {tag}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          <Divider className="border-border-light" />

          {/* Editing Tips */}
          <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <Text className="text-text-secondary text-sm">
              <strong>✏️ Editing Tips:</strong>
              <br />
              • Review your content for clarity and flow
              • Update tags to reflect current content
              • Consider adding new examples or insights
              • Check for spelling and grammar
              • Ensure all links and images still work
            </Text>
          </div>

          {/* Submit Section */}
          <div className="pt-6 border-t border-border-light">
            <div className="flex justify-center space-x-4">
              <Button
                type="default"
                onClick={handleClose}
                size="large"
                icon={<CloseOutlined />}
                className="h-12 px-6"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={updating}
                disabled={!hasChanges}
                size="large"
                icon={<SaveOutlined />}
                className="h-12 px-8 bg-accent-blue hover:bg-accent-blue/90 border-accent-blue hover:border-accent-blue/90 rounded-lg font-semibold shadow-soft hover:shadow-medium transition-all duration-200"
              >
                {updating ? 'Saving Changes...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default EditBlogForm;